import { signInWithRedirect, createAuthHubListener } from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect, useCallback } from 'react'

import authConfigDataHolder from '@src/modules/auth/auth-config.holder'
import { Icon } from '@src/quince-components/src'
import LoaderWrapper from '@src/quince-components/src/atoms/loaderWrapper/LoaderWrapper'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { clearStaleLogoutBroadcastForSignIn } from '@src/utils/cross-domain-auth.helper'

import { getSanitizedRedirectUrls, isOAuthCallbackOnUrl } from '../utils'

import GoogleSvg from './google_log.inline.svg'
import styles from './googleSignIn.module.scss'

const GOOGLE = 'Google'
const REDIRECT_URI = 'redirecturi'

const GoogleSignIn = () => {
  const { t } = useTranslation('common')
  const [isLoading, setIsLoading] = useState(false)

  // Google redirects back to /log-in?code=... as a fresh page load, so this button would
  // otherwise render as an interactive "Continue with Google" while OAuth completion runs.
  // Start it in the loading state on a callback mount to avoid that flash (SE-1425).
  // Set in an effect rather than the initial state to keep SSR/hydration in sync.
  // On success the page redirects away and the loader unmounts with it; if the OAuth
  // round-trip fails, reset here so the button doesn't stay stuck loading.
  useEffect(() => {
    if (!isOAuthCallbackOnUrl()) {
      return
    }
    setIsLoading(true)
    const unsubscribe = createAuthHubListener(({ event, data }) => {
      if (event === 'signInWithRedirect_failure') {
        // `data` carries the original OAuth failure error (Amplify Hub payload); log it, falling
        // back to a synthetic error only when the payload has none.
        captureException(data ?? new Error('signInWithRedirect_failure'), {
          module: MODULES.UNIVERSAL_AUTH,
          feature: FEATURES.UNIVERSAL_AUTH,
          additionalTags: { login_method: 'google', user_type: 'd2c', error_type: 'signInWithRedirect_failure' },
        })
        setIsLoading(false)
      }
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const eventListener = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsLoading(false)
      }
    }

    window.addEventListener('pageshow', eventListener)
    return () => {
      window.removeEventListener('pageshow', eventListener)
    }
  }, [])

  const onGoogleSignInButtonClick = useCallback(() => {
    clearStaleLogoutBroadcastForSignIn()
    const redirectUri = getSanitizedRedirectUrls()
    setIsLoading(true)
    if (redirectUri) {
      // eslint-disable-next-line @quince/no-direct-sessionstorage
      sessionStorage.setItem(REDIRECT_URI, JSON.stringify(redirectUri))
    }
    // Preserve source=b2b across the OAuth round-trip. Cognito replaces all URL params
    // with ?code=...&state=... on callback, so source=b2b is lost from the URL.
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('source') === 'b2b') {
      // eslint-disable-next-line @quince/no-direct-sessionstorage
      sessionStorage.setItem('_oauth_source', 'b2b')
    }
    // Ensure the regional pool config (including oauth.domain) is loaded before starting
    // the OAuth redirect — prevents using the static fallback domain for non-default regions.
    void authConfigDataHolder.getInitPromise().then(() => {
      signInWithRedirect(GOOGLE).catch((e: unknown) => {
        captureException(e, {
          module: MODULES.UNIVERSAL_AUTH,
          feature: FEATURES.UNIVERSAL_AUTH,
          additionalTags: { login_method: 'google', user_type: 'd2c', error_type: 'redirect_start_failed' },
        })
        console.error('Error while calling signInWithRedirect, onGoogleSignInButtonClick() runtime:: ', e)
      })
    })
  }, [])

  return (
    <div
      onClick={onGoogleSignInButtonClick}
      className={isLoading ? styles.googleSignInButton__loading : styles.googleSignInButton}
    >
      {isLoading ? (
        <LoaderWrapper
          loadWrapperClassName={styles.pageLoader}
          showLoader
          containerClassName={styles.containerPageLoader}
        />
      ) : (
        <>
          <Icon iconSvg={GoogleSvg} className={styles.icon} />
          <div className={styles.loginText}>{t('continue_with_google')}</div>
        </>
      )}
    </div>
  )
}

export { GoogleSignIn }
